import { useMemo } from "react";
import type { ApexOptions } from "apexcharts";
import { t, useI18n } from "@bookingcom/lingojs-react";
import type { AverageDiscountChartProps } from "./AverageDiscountChart";

export const useAverageDiscountChartController = ({ euroMetric, percentageMetric }: AverageDiscountChartProps) => {
  const i18n = useI18n();
  const averageDiscountEuro = Math.round(euroMetric.average.value);
  const averageDiscountPercentage = percentageMetric.average.value.toFixed(1);

  const categories = useMemo(
    () => euroMetric.timeSeries.dataPoints.map((dp) => new Date(dp.time).getTime()),
    [euroMetric.timeSeries.dataPoints]
  );

  const series: ApexAxisChartSeries = useMemo(
    () => [
      {
        name: i18n.trans(t("flights_airline_portal_realtime_performance_metric_two_yaxis_a")),
        type: "bar",
        data: euroMetric.timeSeries.dataPoints.map((dp) => dp.value),
      },
      {
        name: i18n.trans(t("flights_airline_portal_realtime_performance_metric_two_yaxis_b")),
        type: "line",
        data: percentageMetric.timeSeries.dataPoints.map((dp) => dp.value),
      },
    ],
    [euroMetric.timeSeries.dataPoints, percentageMetric.timeSeries.dataPoints, i18n]
  );

  const options: ApexOptions = useMemo(
    () => ({
      chart: {
        type: "line",
        toolbar: { show: false },
        zoom: { enabled: false },
      },
      stroke: {
        curve: "straight",
        width: [0, 3],
      },
      colors: ["var(--bui_color_action_foreground)", "var(--bui_color_callout_background)"],
      fill: {
        opacity: [1, 1],
      },
      markers: {
        size: [0, 4],
        colors: ["var(--bui_color_action_foreground)", "var(--bui_color_callout_background)"],
        strokeColors: ["var(--bui_color_action_foreground)", "var(--bui_color_callout_background)"],
        strokeWidth: 0,
        hover: { size: 6 },
      },
      plotOptions: {
        bar: {
          columnWidth: "60%",
          borderRadius: 0,
        },
      },
      xaxis: {
        type: "category",
        categories,
        tickPlacement: "on",
        tickAmount: 6,
        title: {
          text: i18n.trans(t("flights_airline_portal_realtime_performance_metric_two_xaxis")),
          offsetY: 24,
        },
        labels: {
          rotate: 0,
          rotateAlways: false,
          formatter: (value: string): string[] => {
            const date = new Date(Number(value));
            return [
              `${date.getDate()} ${date.toLocaleDateString("en-US", { month: "short" })}`,
              `${date.getFullYear()}`,
            ];
          },
        },
        axisBorder: { show: false },
        axisTicks: { show: false },
        crosshairs: { show: false },
        tooltip: { enabled: false },
      },
      yaxis: [
        {
          tickAmount: 6,
          labels: {
            formatter: (val: number) => `€${Math.round(val)}`,
            padding: 40,
          },
        },
        {
          opposite: true,
          labels: {
            formatter: (val: number) => `${Math.round(val)}%`,
            padding: 40,
          },
        },
      ],
      grid: {
        borderColor: "var(--bui_color_border_alt)",
        strokeDashArray: 0,
        xaxis: { lines: { show: false } },
        yaxis: { lines: { show: true } },
        padding: { top: 24, left: 44, right: 44, bottom: 30 },
      },
      tooltip: {
        x: { format: "d MMM yyyy" },
        y: {
          formatter: (val: number, opts) => {
            if (opts.seriesIndex === 0) {
              return `€${Math.round(val)}`;
            }
            return `${val.toFixed(1)}%`;
          },
        },
      },
      legend: {
        show: true,
        position: "top",
        horizontalAlign: "right",
        markers: { size: 7, shape: "circle" },
        offsetY: 0,
        onItemClick: { toggleDataSeries: false },
      },
    }),
    [categories, i18n]
  );

  return {
    averageDiscountEuro,
    averageDiscountPercentage,
    options,
    series,
  };
};
